import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

// honeypot: a field real applicants never see or fill in, but form-filling
// bots often do — any value here means the submission gets silently dropped.
const EMPTY_FORM = { first_name: '', last_name: '', phone: '', email: '', website: '' };

export default function JobDetail() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [resume, setResume] = useState(null);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setStatus(null);
    setForm(EMPTY_FORM);
    setResume(null);
    setConsent(false);
    API.get(`/careers/${slug}`)
      .then(r => setJob(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (resume) data.append('resume', resume);
      await API.post(`/careers/${job.id}/apply`, data);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (!loading && (notFound || !job)) {
    return (
      <PageLayout>
        <PageBanner en="CAREERS" title={t('careers.notFoundTitle')} breadcrumbs={[{ label: t('careers.title'), to: '/careers' }, { label: t('careers.notFoundTitle') }]} />
        <section className="py-20 text-center">
          <p className="text-gray-400 text-lg mb-6">{t('careers.notFoundMessage')}</p>
          <Link to="/careers" className="btn-primary text-sm">{t('careers.backToCareers')}</Link>
        </section>
      </PageLayout>
    );
  }

  const requirements = (job?.requirements || '').split('\n').map(l => l.trim()).filter(Boolean);

  // Google for Jobs reads this from JobPosting structured data specifically —
  // without it, the listing won't be eligible for rich results in search.
  // employmentType maps to schema.org's fixed enum (not free text).
  const EMPLOYMENT_TYPE_MAP = {
    'Full-time': 'FULL_TIME', 'Part-time': 'PART_TIME', 'Contract': 'CONTRACTOR', 'Internship': 'INTERN',
  };
  const structuredData = job ? {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description || job.title,
    identifier: job.code_number ? { '@type': 'PropertyValue', name: 'Sankyo Machine Tools', value: job.code_number } : undefined,
    datePosted: job.created_at,
    validThrough: new Date(new Date(job.created_at).getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    employmentType: EMPLOYMENT_TYPE_MAP[job.employment_type] || undefined,
    hiringOrganization: { '@type': 'Organization', name: 'Sankyo Machine Tools Philippines Inc.' },
    jobLocation: job.location ? { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: job.location, addressCountry: 'PH' } } : undefined,
  } : null;

  return (
    <PageLayout>
      <SEO title={job?.title} description={job?.description} structuredData={structuredData} />
      <PageBanner en="CAREERS"
        title={job?.title || ''}
        breadcrumbs={[{ label: t('careers.title'), to: '/careers' }, { label: job?.title || '' }]}
      />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-5xl mx-auto">
          <Link to="/careers" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mb-8">
            <ArrowLeftIcon className="w-3.5 h-3.5" /> {t('careers.backToCareers')}
          </Link>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 animate-pulse">
              <div className="h-40 bg-gray-100" />
              <div className="lg:col-span-3 h-64 bg-gray-100" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 border border-gray-200 p-5">
                  <h1 className="text-lg font-bold text-accent mb-1">{job.title}</h1>
                  {job.location && (
                    <p className="flex items-center gap-1.5 text-sm text-gray-500 mb-4">
                      <MapPinIcon className="w-4 h-4" /> {job.location}
                    </p>
                  )}
                  {job.code_number && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('careers.codeNumber')}</p>
                      <p className="font-mono text-sm font-bold text-primary">{job.code_number}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Main content */}
              <div className="lg:col-span-3">
                {requirements.length > 0 && (
                  <div className="mb-10">
                    <h2 className="section-title text-lg mb-4">{t('careers.requirements')}</h2>
                    <ul className="space-y-1.5">
                      {requirements.map((r, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                          <span className="w-1.5 h-1.5 bg-accent shrink-0 mt-[7px]" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {job.description && (
                  <div className="mb-10">
                    <h2 className="section-title text-lg mb-4">{t('careers.overview')}</h2>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">{job.description}</p>
                  </div>
                )}

                {/* Apply form */}
                <div>
                  <h2 className="section-title text-lg mb-4">{t('careers.apply')}</h2>

                  {status === 'success' ? (
                    <div className="bg-green-50 border border-green-200 p-6 flex items-start gap-3">
                      <CheckCircleIcon className="w-6 h-6 text-green-500 shrink-0" />
                      <div>
                        <p className="font-semibold text-green-800">{t('careers.applySuccessTitle')}</p>
                        <p className="text-sm text-green-700 mt-1">{t('careers.applySuccessMessage')}</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
                      {/* Honeypot — hidden from real users via CSS + off-screen, so a
                          screen reader never announces it, but bots that blindly fill
                          every input tend to fill this too. */}
                      <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                        <label htmlFor="job-website">Website</label>
                        <input id="job-website" type="text" tabIndex={-1} autoComplete="off"
                          value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{t('careers.firstName')}</label>
                          <input required value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                            className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{t('careers.lastName')}</label>
                          <input required value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                            className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{t('careers.mobileNumber')}</label>
                          <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                            className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{t('careers.email')}</label>
                          <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                            className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">{t('careers.uploadResume')}</label>
                        <input type="file" accept=".pdf,.doc,.docx" onChange={e => setResume(e.target.files[0] || null)}
                          className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer border border-gray-200 px-1 py-1" />
                      </div>
                      <label className="flex items-start gap-2.5 text-xs text-gray-600">
                        <input type="checkbox" required checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" />
                        {t('careers.consentText')}
                      </label>
                      {status === 'error' && <p className="text-red-500 text-sm">{t('careers.applyError')}</p>}
                      <button type="submit" disabled={status === 'loading'}
                        className="btn-primary w-full sm:w-auto text-sm tracking-widest uppercase disabled:opacity-60">
                        {status === 'loading' ? t('careers.submitting') : t('careers.submit')}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
